import { memoizeFunction } from "@fluentui/react";
import {
  IDateTimeBox,
  IDropDown,
  IFormField,
  IFormatableField,
  ITextField,
  IUIItem,
  dateTimeUtils,
} from "cayo.ui";
import jexl from "jexl";
import { isObjectSelectionGrid } from "@cayo/core/admin-components/grids/ObjectSelectionGrid/types";
import { IJsonForm } from "@cayo/shared/api/types/schema";
import auxPropsUtils from "@cayo/shared/components/Form/modelUtils/auxPropsUtils";
import { IFormModel } from "@cayo/shared/components/Form/modelUtils/types";
import { TGenerationRulesList } from "@cayo/shared/components/Form/types";
import { bindingsUtils } from "@cayo/shared/scheme/bindings";
import { evaluateODataLogicalExpression } from "@cayo/shared/scheme/transforms/odataLogicalExpressionEvaluator";
import { isString } from "@cayo/shared/types/typeGuards";
import { nameofFactory } from "@cayo/shared/utils/object";
import modelUtils from ".";
import { getFlatItems, isPicker, isPropertyGroup } from "./build-model";
import fieldTypes from "./fieldTypes";
import { getValueConverter } from "./get-value-converter";
import { isPropChanged } from "./utils";
import { IObjectLink, IObjectPicker } from "../../../api/schema.api";
import { parseGenerationRule } from "../../../scheme/transforms/generation-rule-parser";
import odataAnnotationsUtils from "../odata-annotations.utils";
import { formComponentTypes } from "../renderers/form-renderers";

export const itemPropNames = nameofFactory<IFormField>();

const isVisible = (item: IUIItem, model: any) => {
  const formField = item as IFormField;

  if (
    formField.readOnly &&
    formField.hideWhenEmpty &&
    !formField.isStandalone &&
    !(formField as IObjectLink).isSoftLink &&
    !isPropertyGroup(item)
  ) {
    const propPath = formField.modelProperty ?? formField.name!;
    const value = model[propPath];
    if (value === undefined || (Array.isArray(value) && value.length === 0)) {
      return false;
    }
  }

  let result = true;
  const keys = item.bindings && Object.keys(item.bindings);
  const key = keys?.find((p) => p === itemPropNames("visible"));

  if (key) {
    const expression = key && item.bindings![key];
    if (expression) {
      result = evaluateODataLogicalExpression(expression, model) ?? true;
    }
  }

  return result;
};

export const getVisibleItems = memoizeFunction(
  (items: IUIItem[], model: any, parentModel?: any) => {
    const result = items?.filter((i) => {
      const result = isVisible(i, parentModel || model);
      return result;
    });
    return result;
  }
);

export const getEditableItems = (items: IFormField[], model: IFormModel) => {
  let result = items?.filter((i) => {
    if ((i as ITextField).possibleValues?.length) {
      return true;
    }

    // if it will not work in the Guardian then fix it there
    if (i.readOnly && i.required) {
      return true;
    }

    // workaround for #32666
    if (isObjectSelectionGrid(i) && i?.allowRemove) {
      return true;
    }

    const result =
      !i.readOnly &&
      (!i.disabled || i.name === "@odata.type") &&
      i.type !== "text" &&
      !i.isStandalone &&
      !auxPropsUtils.isPropDisabled(model, i.name!) &&
      !isPropertyGroup(i);

    return result;
  });

  result = getVisibleItems(result, model);

  const propGroups = items?.filter(isPropertyGroup) as IFormatableField[];
  if (propGroups?.length) {
    for (const group of propGroups.filter((g) => !g.formatter)) {
      const items = getEditableItems((group as any)?.items, model);
      const index = result.findIndex((i) => i === group);
      result.splice(index, 0, ...items);
      //result.splice(index, 0, );
    }
  }

  return result || [];
};

const createProps = (model: any, propPath: string, propValue: any) => {
  const paths = modelUtils.splitPropPath(propPath);
  let newModel = model;
  for (let i = 0; i < paths.length - 1; i++) {
    const path = paths[i];

    if (!newModel[path]) {
      newModel[path] = {};
    }

    newModel = newModel[path];
  }

  newModel[paths[paths.length - 1]] = propValue;
};

const flatModel = (model: any) => {
  const flattingKeys = Object.keys(model).filter((k) => k.indexOf(".") > 0);
  for (const key of flattingKeys) {
    createProps(model, key, model[key]);
    if (!(key.split(".").length === 2 && key.endsWith("@odata.type"))) {
      delete model[key];
    }
  }
};

export const getModel = (form: IJsonForm, model: IFormModel) => {
  if (!model || !form.items?.length) {
    return model;
  }

  const items = getFlatItems(form.items);
  const editableItems = getEditableItems(items, model);
  const visibleItems = getVisibleItems(items, model);

  let newModel: any = {};
  for (const item of editableItems) {
    const { name, type, dataType, valueTemplate } = item;
    const modelProperty = (item as any).modelProperty || name;
    const value = model[modelProperty!];
    const compType = type as formComponentTypes;

    const isSimpleProp = modelProperty.indexOf(".") === -1;
    const isArray = Array.isArray(value);

    if (
      form.sendAllFields !== true &&
      (isSimpleProp || form.isAdminProps) &&
      isPropChanged(model, modelProperty) === false
    ) {
      continue;
    }

    const converter = getValueConverter(item).from;
    if (converter) {
      const convertedValue = converter(value);
      if (item.dataType && convertedValue) {
        const dataTypeProp = odataAnnotationsUtils.getOdataProp(name!, "odata.type");
        newModel![dataTypeProp] = item.dataType;
      }
      newModel[modelProperty!] = convertedValue;
    } else {
      const picker = isPicker(item) ? (item as IObjectPicker) : undefined;
      const dateTimeBox = item as IDateTimeBox;

      if (
        (dataType === fieldTypes.string ||
          (dataType === fieldTypes.softLink && picker?.multiselect === false)) &&
        isArray
      ) {
        // picker
        const isSimplePicker = picker?.isPicker && !picker?.columns?.length;

        newModel[modelProperty!] =
          value.length > 0 ? model[modelProperty!][0] : isSimplePicker ? "" : undefined;
      } else if (compType === "checkbox" && !value) {
        // boolean
        newModel[modelProperty!] = false;
      } else if (compType === "datetimebox" && dateTimeBox?.editMode === "onlyTime") {
        // date time box
        newModel[modelProperty!] = dateTimeUtils.convertTimeToUtc(model[modelProperty!]);
      } else if (picker?.valueTemplate && isArray && value.length === 1) {
        // TODO: non-single value
        newModel[modelProperty!] = { ...value[0], IsEnabled: true };
      } else if (valueTemplate && isString(valueTemplate)) {
        const optionContext = (item as IDropDown).options.find((option) => option.key === value);
        const newValue = jexl.evalSync(valueTemplate, optionContext);
        newModel[modelProperty!] = newValue;
        // #32867 TODO: this will probably need to be removed when ui-scheme provides boundProperties
      } else if (type === "spn-grid") {
        const expandedKey = `${name}_Expanded`;
        if (expandedKey in model && isPropChanged(model, expandedKey) !== false) {
          newModel[expandedKey] = model[expandedKey];
        }
      } else {
        newModel[modelProperty!] = value;
      }
    }
  }

  const generationRulesList = model.generationRulesList as TGenerationRulesList;

  for (const genProp in generationRulesList) {
    // skip if newModel already has this property
    const newValue = newModel[genProp];
    if (newValue) {
      continue;
    }

    // simply set a property to a newModel if it exists
    const value = model[genProp];
    if (value) {
      newModel[genProp] = value;
      continue;
    }

    // if there is no property then evaluate it and set to newModel
    const { generationRule } = generationRulesList[genProp];
    const parsedGrValue = parseGenerationRule(generationRule, model);
    newModel[genProp] = parsedGrValue;
  }

  for (const a of editableItems!.filter((a) => !!a.valueDependsOn)) {
    const resolvedValue = bindingsUtils.resolveExpression(model, a.valueDependsOn!);
    newModel[a.name!] = resolvedValue;
  }

  newModel = auxPropsUtils.removeAuxProperties(newModel);

  flatModel(newModel);

  if (form.boundProperties) {
    const visibleItemsNames = new Set(visibleItems.map((item) => item.name));
    for (const boundProperties of form.boundProperties) {
      // set all bound properties to newModel no matter was it changed or not
      for (const boundProp of boundProperties) {
        if (boundProp in model && visibleItemsNames.has(boundProp)) {
          newModel[boundProp] = model[boundProp];
        }
      }
    }
  }

  if (form.submit?.method === "POST" && form.targetType && !newModel["@odata.type"]) {
    newModel["@odata.type"] = form.targetType;
  }

  // Add customAttrN_Def
  Object.keys(model)
    .filter((k) => k.endsWith("_Def"))
    .forEach((k) => {
      const customAttrDef = model[k];
      if (customAttrDef) {
        newModel[k] = customAttrDef;
      }
    });

  return newModel;
};
